import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import { uploadMediaWithProgress } from "../../data/api/media/mutators";
import { queryKeys } from "../../data/queries/query-keys";
import type { Media } from "../../data/api/media/types";

export type UploadStatus = "pending" | "uploading" | "complete" | "error";

export interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: UploadStatus;
  url?: string;
  error?: string;
  previewUrl?: string;
}

interface UseUploadWithProgressOptions {
  productId: string;
  userId: string;
}

interface UploadSuccessResult {
  success: true;
  id: string;
  media: Media;
}

interface UploadErrorResult {
  success: false;
  id: string;
  error: Error;
}

export type UploadResult = UploadSuccessResult | UploadErrorResult;

interface RetrySuccessResult {
  success: true;
  media: Media;
}

interface RetryErrorResult {
  success: false;
  error: Error;
}

type RetryResult = RetrySuccessResult | RetryErrorResult;

interface UseUploadWithProgressReturn {
  uploads: Record<string, UploadItem>;
  uploadList: UploadItem[];
  uploadFiles: (files: FileList | File[]) => Promise<PromiseSettledResult<UploadResult>[]>;
  retryUpload: (id: string) => Promise<RetryResult | undefined>;
  removeUpload: (id: string) => void;
  clearCompleted: () => void;
  isUploading: boolean;
  pendingCount: number;
  uploadingCount: number;
  completeCount: number;
  errorCount: number;
  overallProgress: number;
}

const useUploadWithProgress = ({
  productId,
  userId,
}: UseUploadWithProgressOptions): UseUploadWithProgressReturn => {
  const [uploads, setUploads] = useState<Record<string, UploadItem>>({});
  const queryClient = useQueryClient();

  const updateUpload = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploads((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...updates },
    }));
  }, []);

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<PromiseSettledResult<UploadResult>[]> => {
      const fileArray = Array.from(files);

      // Create upload entries for all files
      const newUploads: Record<string, UploadItem> = {};
      fileArray.forEach((file) => {
        const id = uuid();
        const previewUrl = URL.createObjectURL(file);
        newUploads[id] = {
          id,
          file,
          progress: 0,
          status: "pending",
          previewUrl,
        };
      });

      setUploads((prev) => ({ ...prev, ...newUploads }));

      // Upload each file
      const uploadPromises = Object.entries(newUploads).map(
        async ([id, upload]): Promise<UploadResult> => {
          updateUpload(id, { status: "uploading" });

          try {
            const media = await uploadMediaWithProgress(
              {
                id,
                file: upload.file,
                productId,
                userId,
              },
              (progress) => {
                updateUpload(id, { progress });
              },
            );

            updateUpload(id, {
              status: "complete",
              progress: 100,
              url: media.url,
            });

            return { success: true, id, media };
          } catch (error) {
            const errorObj = error instanceof Error ? error : new Error("Upload failed");
            updateUpload(id, {
              status: "error",
              error: errorObj.message,
            });
            return { success: false, id, error: errorObj };
          }
        },
      );

      const results = await Promise.allSettled(uploadPromises);

      // Invalidate media query and wait for refetch before clearing uploads
      await queryClient.invalidateQueries({
        queryKey: queryKeys.media.productList(productId),
      });

      // Clear completed uploads — they're now in the media list
      setUploads((prev) => {
        const remaining: Record<string, UploadItem> = {};
        for (const [id, upload] of Object.entries(prev)) {
          if (upload.status === "complete") {
            if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
          } else {
            remaining[id] = upload;
          }
        }
        return remaining;
      });

      return results;
    },
    [productId, userId, updateUpload, queryClient],
  );

  const retryUpload = useCallback(
    async (id: string): Promise<RetryResult | undefined> => {
      const upload = uploads[id];
      if (!upload || upload.status !== "error") return;

      updateUpload(id, { status: "uploading", progress: 0, error: undefined });

      try {
        const media = await uploadMediaWithProgress(
          {
            id,
            file: upload.file,
            productId,
            userId,
          },
          (progress) => {
            updateUpload(id, { progress });
          },
        );

        updateUpload(id, {
          status: "complete",
          progress: 100,
          url: media.url,
        });

        // Invalidate media query to refresh the list
        queryClient.invalidateQueries({
          queryKey: queryKeys.media.productList(productId),
        });

        return { success: true, media };
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error("Upload failed");
        updateUpload(id, {
          status: "error",
          error: errorObj.message,
        });
        return { success: false, error: errorObj };
      }
    },
    [uploads, productId, userId, updateUpload, queryClient],
  );

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const upload = prev[id];
      // Revoke the preview URL to free memory
      if (upload?.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearCompleted = useCallback(() => {
    setUploads((prev) => {
      const remaining: Record<string, UploadItem> = {};
      Object.entries(prev).forEach(([id, upload]) => {
        if (upload.status !== "complete") {
          remaining[id] = upload;
        } else if (upload.previewUrl) {
          URL.revokeObjectURL(upload.previewUrl);
        }
      });
      return remaining;
    });
  }, []);

  // Computed values
  const uploadList = Object.values(uploads);
  const pendingCount = uploadList.filter((u) => u.status === "pending").length;
  const uploadingCount = uploadList.filter((u) => u.status === "uploading").length;
  const completeCount = uploadList.filter((u) => u.status === "complete").length;
  const errorCount = uploadList.filter((u) => u.status === "error").length;
  const isUploading = uploadingCount > 0 || pendingCount > 0;

  // Overall progress (average of all uploads)
  const overallProgress =
    uploadList.length > 0
      ? uploadList.reduce((sum, u) => sum + u.progress, 0) / uploadList.length
      : 0;

  return {
    uploads,
    uploadList,
    uploadFiles,
    retryUpload,
    removeUpload,
    clearCompleted,
    isUploading,
    pendingCount,
    uploadingCount,
    completeCount,
    errorCount,
    overallProgress,
  };
};

export default useUploadWithProgress;
